/**
 * Fragment shader liquid-glass. Berisi penanda `//__NOISE__` yang diganti isi NOISE_GLSL oleh orb-webgl-shaders.ts (GLSL tidak punya #include).
 *
 * Ditulis sebagai string TS, bukan berkas `.glsl?raw`: impor `?raw` hanya
 * dipahami Vite (dev), sedangkan build produksi memakai `bun build` yang
 * gagal meresolvenya. Isi string di bawah harus tetap GLSL apa adanya
 * (`#version` harus di awal string, tanpa baris kosong di depan).
 */
export const LIQUID_GLASS_FRAG_TEMPLATE = `#version 300 es
precision highp float;
out vec4 fragColor;

uniform vec2 uResolution;
// gl_FragCoord SELALU relatif ke seluruh framebuffer, bukan ke viewport yang
// sedang aktif — wajib dikurangi pojok-kiri-bawah viewport (sama dengan x,y
// yang dikirim ke gl.viewport()/gl.scissor()) sebelum dipakai, kalau tidak
// semua orb di luar sprite pertama gagal tergambar. Lihat orb-webgl-stage.ts.
uniform vec2 uViewportOrigin;
uniform float uTime;
uniform float uDistortion; // kekuatan lipatan permukaan, 0..1
uniform float uSpeed;      // kecepatan animasi, 0..1.5
uniform float uFlow;       // skala pola warna mengalir, 1..4
uniform float uSparkle;    // kekuatan 3 titik kilau, 0..2
// 4 titik warna siklis diturunkan dari colorFrom/colorTo admin di
// orb-webgl-palette.ts — BUKAN palet tertanam di shader.
uniform vec3 uColor0;
uniform vec3 uColor1;
uniform vec3 uColor2;
uniform vec3 uColor3;
// Peredupan anti burn-in (idleBrightness di orb-motion.ts) — sama persis
// dengan varian Canvas 2D, supaya liquid-glass tidak jadi lubang yang
// melewati proteksi.
uniform float uBrightness;

//__NOISE__

/** Mencampur 4 warna uniform secara siklis — polanya sama dengan prototipe, nilainya kini dari admin. */
vec3 palette(float t) {
  float u = fract(t) * 4.0;
  float seg = floor(u);
  float f = smoothstep(0.0, 1.0, fract(u));
  if (seg < 1.0) return mix(uColor0, uColor1, f);
  if (seg < 2.0) return mix(uColor1, uColor2, f);
  if (seg < 3.0) return mix(uColor2, uColor3, f);
  return mix(uColor3, uColor0, f);
}

/** 3 titik kilau nyaris-putih yang bergerak di permukaan — bukan env map, posisinya murni fungsi waktu. */
vec3 specDots(vec3 n, float t) {
  vec3 total = vec3(0.0);
  for (int i = 0; i < 3; i++) {
    float fi = float(i);
    float a = t * (0.55 + fi * 0.17) + fi * 2.0944; // spasi 120°, kecepatan sedikit beda tiap titik
    float bob = sin(t * 0.5 + fi * 1.7) * 0.35;
    vec3 lightDir = normalize(vec3(cos(a) * 0.82, bob + 0.3, sin(a) * 0.82));
    float d = max(dot(n, lightDir), 0.0);
    total += pow(d, 42.0) * vec3(1.0, 0.97, 0.94);
  }
  return total;
}

void main() {
  vec2 fragLocal = gl_FragCoord.xy - uViewportOrigin;
  vec2 uv = (fragLocal - 0.5 * uResolution) / min(uResolution.x, uResolution.y);
  uv *= 2.06; // sedikit ruang margin di luar bola untuk glow tepi
  float r2 = dot(uv, uv);
  if (r2 > 1.0) {
    fragColor = vec4(0.0);
    return;
  }
  float z = sqrt(1.0 - r2);
  vec3 n0 = vec3(uv, z); // titik di bola satuan = normal dasar (kamera di +Z)

  float t = uTime * uSpeed;

  // Distorsi permukaan: fbm domain-warped memutar normal dasar.
  vec3 warpP = n0 * 2.15 + vec3(0.0, 0.0, t * 0.65);
  vec3 warp = vec3(fbm(warpP + 1.7), fbm(warpP + 9.2), fbm(warpP + 4.1));
  vec3 n = normalize(n0 + uDistortion * (warp - 0.5) * 1.5);

  // Warna: fbm domain-warped dievaluasi pada NORMAL YANG SUDAH TERDISTORSI,
  // supaya pita warna mengikuti lipatan cairan — menyapu seluruh bola, bukan
  // terpusat atau hanya di tepi (fresnel di bawah cuma aksen tipis).
  vec3 colorP = n * uFlow + vec3(0.0, 0.0, t * 0.6);
  float field = fbm(colorP + (fbm(colorP * 1.35 + 11.0) - 0.5) * 1.7);
  float fieldAlt = fbm(colorP * 0.55 - 5.0 + vec3(0.0, 0.0, t * 0.35));
  vec3 col = palette(field + fieldAlt * 0.22);
  col *= 0.82 + 0.35 * fieldAlt; // variasi terang-gelap antar pita, bukan rata

  // Fresnel: AKSEN TIPIS saja (highlight tepi), tidak menentukan hue.
  float fres = pow(1.0 - max(dot(n, vec3(0.0, 0.0, 1.0)), 0.0), 3.0);
  col += fres * 0.18;

  col += specDots(n, t) * uSparkle;
  col *= uBrightness;

  float edgeAA = 1.0 - smoothstep(0.978, 1.0, r2);
  // Keluaran PREMULTIPLIED (konteks premultipliedAlpha:true di orb-webgl-stage.ts).
  fragColor = vec4(col * edgeAA, edgeAA);
}
`;
